import {
  MinusCircleOutlined,
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Upload,
} from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import moment from 'moment';
import { ChangeOrder } from '../../modules/change-orders';
import Title from 'antd/lib/typography/Title';
import { Contract } from '../../modules/contracts';
import { useEffect, useState } from 'react';

const { Option } = Select;

interface PCCOCreateUpdateComponentProps {
  data?: ChangeOrder;
  primeContract: Contract;
  gongjongs: any[];
  uploadProps: any;
  onFinish: (changeOrder: ChangeOrder) => void;
  onCancel: () => void;
}

function PCCOCreateUpdateComponent({
  data,
  primeContract,
  gongjongs,
  uploadProps,
  onFinish,
  onCancel,
}: PCCOCreateUpdateComponentProps) {
  const [form] = Form.useForm();
  const [gongjongOptions, setGongjongOptions] = useState<any>();

  useEffect(() => {
    let children: any = [];
    if (gongjongs) {
      gongjongs.sort((a: any, b: any) => a.name.localeCompare(b.name));
      for (let i = 0; i < gongjongs.length; i++) {
        children.push(
          <Option key={i} value={gongjongs[i].name}>
            {gongjongs[i].name}
          </Option>,
        );
      }
      setGongjongOptions(children);
    }
    children = [];
  }, [gongjongs]);

  let title: string = '';
  let tempData: any;
  if (!!data) {
    title = '설계변경 수정';
    tempData = {
      ...data,
      changeDate:
        data?.changeDate === ''
          ? moment()
          : moment(data?.changeDate, 'YYYY-MM-DD'),
    };
  } else {
    title = '설계변경 생성';
    tempData = {
      title:
        primeContract.title + ' 설계변경 - ' + moment().format('YYYY/MM/DD'),
      file: [],
    };
    const priceData = {
      version: '2024-01-08',
      isComplex: false,
      priceData: {
        directCosts: [],
        totalDirectCosts: 0,
        totalIndirectCosts: 0,
        costTotal: 0,
        administrativeExpense: 0,
        profit: 0,
        supplyPrice: 0,
        vat: 0,
        totalPrice: 0,
      },
    };
    tempData.priceData = priceData;
  }

  const normFile = (e: any) => {
    let fileList = e.fileList;

    if (Array.isArray(e)) {
      return e;
    }
    return e && fileList;
  };

  const updatePriceField = () => {
    let totalDirectCosts = 0,
      totalIndirectCosts = 0,
      supplyPrice = 0;
    const isComplex = form.getFieldValue(['priceData', 'isComplex']);
    if (isComplex) {
      totalDirectCosts = form
        .getFieldValue(['priceData', 'priceData', 'directCosts'])
        .reduce((accumulator: number, currentValue: any) => {
          if (currentValue.price === undefined) return accumulator;
          return accumulator + currentValue.price;
        }, 0);
      totalIndirectCosts = form.getFieldValue([
        'priceData',
        'priceData',
        'totalIndirectCosts',
      ]);
      supplyPrice = totalDirectCosts + totalIndirectCosts;
    } else {
      supplyPrice = form.getFieldValue([
        'priceData',
        'priceData',
        'supplyPrice',
      ]);
    }

    const vat = Math.round((supplyPrice * 10) / 100);
    const totalPrice = supplyPrice + vat;

    form.setFieldsValue({
      priceData: {
        priceData: {
          totalDirectCosts,
          totalIndirectCosts,
          supplyPrice,
          vat,
          totalPrice,
        },
      },
    });
  };

  const updateVatField = () => {
    let totalDirectCosts = 0,
      totalIndirectCosts = 0,
      supplyPrice = 0;
    const isComplex = form.getFieldValue(['priceData', 'isComplex']);
    if (isComplex) {
      totalDirectCosts = form
        .getFieldValue(['priceData', 'priceData', 'directCosts'])
        .reduce((accumulator: number, currentValue: any) => {
          if (currentValue.price === undefined) return accumulator;
          return accumulator + currentValue.price;
        }, 0);
      totalIndirectCosts = form.getFieldValue([
        'priceData',
        'priceData',
        'totalIndirectCosts',
      ]);
      supplyPrice = totalDirectCosts + totalIndirectCosts;
    } else {
      supplyPrice = form.getFieldValue([
        'priceData',
        'priceData',
        'supplyPrice',
      ]);
    }

    const vat = form.getFieldValue(['priceData', 'priceData', 'vat']);
    const totalPrice = supplyPrice + vat;

    form.setFieldsValue({
      priceData: {
        priceData: {
          totalDirectCosts,
          totalIndirectCosts,
          supplyPrice,
          vat,
          totalPrice,
        },
      },
    });
  };

  return (
    <>
      <Row>
        <Col flex={1}>
          <Title level={3}>{title}</Title>
        </Col>
      </Row>

      <Title level={4}>일반</Title>
      <Form
        form={form}
        name="dynamic_form_item"
        layout="vertical"
        onFinish={onFinish}
        initialValues={tempData}
      >
        <Row justify="space-between">
          <Col span={11}>
            <Form.Item name="title" label="제목">
              <Input placeholder="제목" />
            </Form.Item>
          </Col>
        </Row>
        <Row justify="space-between">
          <Col span={11}>
            <Form.Item name="description" label="설명">
              <TextArea rows={8} />
            </Form.Item>
          </Col>
          <Col span={11}>
            <Form.Item
              label="변경일"
              name="changeDate"
              rules={[{ required: true, message: '변경 날짜를 입력하세요.' }]}
            >
              <DatePicker format="YYYY-MM-DD" />
            </Form.Item>
          </Col>
        </Row>
        <Row justify="space-between">
          <Col span={11}>
            <Form.Item
              name="file"
              label="첨부 파일"
              valuePropName="fileList"
              getValueFromEvent={normFile}
            >
              <Upload {...uploadProps}>
                <Button icon={<UploadOutlined rev={undefined} />}>
                  파일 올리기
                </Button>
              </Upload>
            </Form.Item>
          </Col>
        </Row>

        <Title level={4}>내역서</Title>

        <Form.Item name={['priceData', 'isComplex']} valuePropName="checked">
          <Checkbox
            style={{ width: '100%' }}
            onChange={() => {
              form.setFieldsValue({
                priceData: {
                  priceData: {
                    directCosts: [],
                    totalDirectCosts: 0,
                    totalIndirectCosts: 0,
                    supplyPrice: 0,
                    vat: 0,
                    totalPrice: 0,
                  },
                },
              });
            }}
          >
            집계내역으로 관리
          </Checkbox>
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.priceData.isComplex !== currentValues.priceData.isComplex
          }
        >
          {({ getFieldValue }) =>
            getFieldValue(['priceData', 'isComplex']) === true ? (
              <>
                <Form.List name={['priceData', 'priceData', 'directCosts']}>
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <Space
                          key={key}
                          style={{ display: 'flex', marginBottom: 8 }}
                          align="baseline"
                        >
                          <Form.Item
                            {...restField}
                            name={[name, 'name']}
                            rules={[
                              { required: true, message: '공정을 선택하세요' },
                            ]}
                          >
                            <Select
                              allowClear
                              style={{ width: '100%' }}
                              placeholder="공정을 선택하세요"
                            >
                              {gongjongOptions}
                            </Select>
                          </Form.Item>
                          <Form.Item
                            {...restField}
                            name={[name, 'price']}
                            rules={[
                              { required: true, message: '금액을 입력하세요' },
                            ]}
                          >
                            <InputNumber
                              placeholder="금액"
                              style={{ width: '100%' }}
                              formatter={(value) =>
                                `₩ ${value}`.replace(
                                  /\B(?=(\d{3})+(?!\d))/g,
                                  ',',
                                )
                              }
                              parser={(value) =>
                                value!.replace(/₩\s?|(,*)/g, '')
                              }
                              onChange={updatePriceField}
                            />
                          </Form.Item>
                          <MinusCircleOutlined
                            onClick={() => {
                              remove(name);
                              updatePriceField();
                            }}
                            rev={undefined}
                          />
                        </Space>
                      ))}
                      <Form.Item>
                        <Button
                          type="dashed"
                          onClick={() => add()}
                          block
                          icon={<PlusOutlined rev={undefined} />}
                        >
                          직접공사비(공종별) 추가
                        </Button>
                      </Form.Item>
                    </>
                  )}
                </Form.List>
                <Row justify={'start'}>
                  <Col span={10}>
                    <Form.Item
                      name={['priceData', 'priceData', 'totalDirectCosts']}
                      label="집계내역 합계"
                      rules={[{ required: true, message: '금액을 입력하세요' }]}
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        formatter={(value) =>
                          `₩ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                        }
                        parser={(value) => value!.replace(/₩\s?|(,*)/g, '')}
                        disabled={true}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row justify={'start'}>
                  <Col span={10}>
                    <Form.Item
                      name={['priceData', 'priceData', 'totalIndirectCosts']}
                      label="기타금액"
                      rules={[{ required: true, message: '금액을 입력하세요' }]}
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        formatter={(value) =>
                          `₩ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                        }
                        parser={(value) => value!.replace(/₩\s?|(,*)/g, '')}
                        onChange={updatePriceField}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            ) : null
          }
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.priceData.isComplex !== currentValues.priceData.isComplex
          }
        >
          {({ getFieldValue }) => (
            <Row justify={'start'}>
              <Col span={10}>
                <Form.Item
                  name={['priceData', 'priceData', 'supplyPrice']}
                  label="공급가액"
                  rules={[{ required: true, message: '금액을 입력하세요' }]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    formatter={(value) =>
                      `₩ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                    }
                    parser={(value) => value!.replace(/₩\s?|(,*)/g, '')}
                    disabled={getFieldValue(['priceData', 'isComplex'])}
                    onChange={updatePriceField}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}
        </Form.Item>

        <Row justify={'start'}>
          <Col span={10}>
            <Form.Item
              name={['priceData', 'priceData', 'vat']}
              label="부가세액"
              rules={[{ required: true, message: '금액을 입력하세요' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                formatter={(value) =>
                  `₩ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                }
                parser={(value) => value!.replace(/₩\s?|(,*)/g, '')}
                onChange={updateVatField}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row justify={'start'}>
          <Col span={10}>
            <Form.Item
              name={['priceData', 'priceData', 'totalPrice']}
              label="총공사금액"
              rules={[{ required: true, message: '금액을 입력하세요' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                formatter={(value) =>
                  `₩ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                }
                parser={(value) => value!.replace(/₩\s?|(,*)/g, '')}
                disabled={true}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row justify="center">
          <Col>
            <Form.Item>
              <Space>
                <Button htmlType="button" onClick={onCancel}>
                  취소
                </Button>
                <Button type="primary" htmlType="submit">
                  제출
                </Button>
              </Space>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </>
  );
}
export default PCCOCreateUpdateComponent;
